import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import { useAuth } from "react-oidc-context";

// Define the UserContext type
interface UserContextType {
  loggedIn: boolean;
  user: User | null;
  setLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  logout: () => void;
}

interface User {
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  id?: string;
  token?: string;
  phoneNumber?: string;
  contactPreference?: 'email' | 'phone' | 'text';
}

export const UserContext = createContext<UserContextType | undefined>(
  undefined
);

// Custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Define the UserProvider props
interface UserProviderProps {
  children: ReactNode;
  redirectUri?: string;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  
  // Get auth state from react-oidc-context
  const auth = useAuth();

  useEffect(() => {
    // When OIDC auth state changes, update our user context
    if (auth.isAuthenticated && auth.user) {
      console.log("Authentication detected, updating user context");
      setLoggedIn(true);

      // Extract all user information from OIDC user profile (id_token)
      const userInfo: User = {
        username: auth.user.profile.sub || "",
        email: auth.user.profile.email || "",
        firstName: auth.user.profile.given_name,
        lastName: auth.user.profile.family_name,
        id: auth.user.profile.sub,
        token: auth.user.id_token,
      };
      
      // Store tokens in localStorage for API calls
      if (auth.user.access_token) {
        localStorage.setItem("access_token", auth.user.access_token);
      }
      
      if (auth.user.id_token) {
        localStorage.setItem("id_token", auth.user.id_token);
      }
      
      if (auth.user.refresh_token) {
        localStorage.setItem("refresh_token", auth.user.refresh_token);
      }
      
      console.log("User profile from token:", userInfo);
      setUser(userInfo);
    } else {
      console.log("No authentication detected, clearing user context");
      setLoggedIn(false);
      setUser(null);
    }
  }, [auth.isAuthenticated, auth.user]);

  // Use useCallback to create a stable logout function
  const logout = useCallback(() => {
    // Clear tokens from localStorage
    localStorage.removeItem("access_token");
    localStorage.removeItem("id_token");
    localStorage.removeItem("refresh_token");
    
    // Clear OIDC user data directly (instead of signoutRedirect which is causing 500 errors)
    auth.removeUser();
    
    // Reset the user state
    setLoggedIn(false);
    setUser(null);
    
    // Redirect to login page manually
    window.location.href = "/login";
  }, [auth]);

  const value = useMemo(
    () => ({
      loggedIn,
      user,
      setLoggedIn,
      setUser,
      logout,
    }),
    [loggedIn, user, logout]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;